import React, { useState, useContext, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  CircularProgress, 
  Box, 
  Fade,
  TablePagination
} from '@mui/material';
import { AccountBalance } from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import { getWalletByUserId, getTransactionsByUserId, getTransactionsByPlaceId, getAllTransactions } from '../../services/financeService';
import UserSearch from '../../components/Admin/UserSearch';
import WalletAdminView from '../../components/Admin/WalletAdminView';

const GestionFinancieraPage = () => {
  const { user } = useContext(AuthContext);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userWallet, setUserWallet] = useState(null);
  const [userTransactions, setUserTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [placeTransactions, setPlaceTransactions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);

  const fetchTransactions = async (currentPage, currentRowsPerPage) => {
    if (user && user.is_admin) {
      const placeId = sessionStorage.getItem('selectedPlaceId');
      setLoading(true);
      setError(null);
      try {
        let response;
        if (placeId && placeId !== 'all') {
          response = await getTransactionsByPlaceId(placeId, currentPage + 1, currentRowsPerPage);
        } else {
          response = await getAllTransactions(currentPage + 1, currentRowsPerPage);
        }
        setPlaceTransactions(response.data || []);
        setTotalTransactions(response.total || 0);
      } catch (err) {
        setError('Error al cargar las transacciones.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchUserTransactions = async (userId, currentPage, currentRowsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      const [walletData, transactionsData] = await Promise.all([
        getWalletByUserId(userId),
        getTransactionsByUserId(userId, currentPage + 1, currentRowsPerPage)
      ]);
      setUserWallet(walletData);
      setUserTransactions(transactionsData.data || []);
      setTotalTransactions(transactionsData.total || 0);
    } catch (err) {
      setError('Error al cargar los datos financieros del usuario.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUser) {
      fetchUserTransactions(selectedUser.id, page, rowsPerPage);
    } else {
      fetchTransactions(page, rowsPerPage);
    }
  }, [user, page, rowsPerPage, selectedUser]);


  const handleUserSelect = (selected) => {
    setPage(0);
    setSelectedUser(selected);
    if (!selected) {
      setUserWallet(null);
      setUserTransactions([]);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const refreshUserData = () => {
    if (selectedUser) {
      fetchUserTransactions(selectedUser.id, page, rowsPerPage);
    } else {
      fetchTransactions(page, rowsPerPage);
    }
  };

  if (!user || !user.is_admin) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography 
          variant="h5" 
          color="error" 
          sx={{ 
            fontWeight: 600,
            color: '#F44336'
          }}
        >
          Acceso denegado. Esta página es solo para administradores.
        </Typography>
      </Container>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box>
        <UserSearch onUserSelect={handleUserSelect} />
        {error && (
          <Box sx={{ mt: 3 }}>
            <Typography 
              color="error" 
              sx={{ 
                fontWeight: 500,
                color: '#F44336',
                textAlign: 'center'
              }}
            >
              {error}
            </Typography>
          </Box>
        )}
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 4 
          }}>
            <CircularProgress 
              sx={{ 
                color: '#002642' 
              }} 
            />
          </Box>
        )}
        {!loading && (
          <WalletAdminView
            userWallet={userWallet}
            userTransactions={selectedUser ? userTransactions : placeTransactions}
            onDataRefresh={refreshUserData}
            selectedUser={selectedUser}
          />
        )}
        <TablePagination
          component="div"
          count={totalTransactions}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Transacciones por página:"
        />
      </Box>
    </Fade>
  );
};


export default GestionFinancieraPage;